/**
 * Third-Party Error Filter
 * Utility untuk memfilter error dari third-party scripts seperti ContentSquare, Google Analytics, dll
 */

// Daftar domain third-party yang error-nya akan di-filter
const THIRD_PARTY_DOMAINS = [
  "contentsquare.net",
  "t.contentsquare.net",
  "google-analytics.com",
  "googletagmanager.com",
  "facebook.net",
  "connect.facebook.net",
  "doubleclick.net",
  "googlesyndication.com",
  "googleadservices.com",
  "hotjar.com",
  "clarity.ms",
  "segment.com",
  "mixpanel.com",
  "amplitude.com",
  "intercom.io",
  "zendesk.com",
  "livechat.com",
  "tawk.to",
  "firebase.com",
  "firebaseio.com",
  "googleapis.com",
  "fcm.googleapis.com",
  "gstatic.com",
  "recaptcha",
  "google.com/recaptcha",
];

// Daftar error message patterns dari third-party
const THIRD_PARTY_ERROR_PATTERNS = [
  "this.o.at is not a function",
  "Cannot read property 'at' of undefined",
  "Cannot read properties of undefined (reading 'at')",
  "Script error",
  "Script error.",
  "ResizeObserver loop",
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",
  // Blob URL errors (browser extensions, service workers)
  "blob:",
  "blob:http",
  "blob:https",
  "@blob:",
  // Firebase Cloud Messaging errors
  "messaging/token-update-failed",
  "messaging/token-subscribe-failed",
  "messaging/token-unsubscribe-failed",
  "messaging/permission-blocked",
  "messaging/permission-default",
  "messaging/notifications-blocked",
  "messaging/failed-service-worker-registration",
  "A problem occurred while updating the user from FCM",
  "Failed to fetch",
  "FirebaseError: Messaging:",
  "Failed to register a ServiceWorker",
  "ServiceWorker",
  "firebase-messaging-sw.js",
  "serviceWorkers.navigator.serviceWorker.register",
  "Error: Rejected",
  // Google Tag Manager / Analytics errors
  "TypeError: Failed to fetch",
  "gtag/js",
  "googletagmanager.com",
  "google-analytics.com",
  "GA4React",
  "GA4React intialization failed",
  "GA4React initialization failed",
  // reCAPTCHA errors
  "reCAPTCHA",
  "recaptcha",
  "gstatic.com/recaptcha",
  "reCAPTCHA Timeout",
  "reCAPTCHA placeholder element must be an element or id",
  "reCAPTCHA has already been rendered",
  // Google Ads / Publisher Tag errors
  "googletag",
  "googletag.pubads",
  "TossPixel",
  "Cannot read properties of undefined (reading 'push')",
  "Cannot read property 'push' of undefined",
  "Cannot read properties of undefined (reading 'dataLayer')",
  "Cannot read property 'dataLayer' of undefined",
  "_gaq",
  "_paq",
  "criteo_q",
  "adroll",
  // SigNoz / OpenTelemetry internal errors
  "BatchSpanProcessorBase",
  "BatchSpanProcessor",
  "Unhandled Promise rejection: Timeout",
  "zone.js",
  "at timer (zone.js",
  "at _ZoneDelegate.invokeTask (zone.js",
  "at Zone.runTask (zone.js",
  "at ZoneTask.invoke (zone.js",
  "runTask@",
  "invokeTask@",
  "ZoneDelegate",
  "Zone.run",
];

/**
 * Check if error is from third-party script
 * @param {Error|string} error - Error object or message
 * @param {string} filename - Source filename (optional)
 * @returns {boolean} - True if error is from third-party
 */
export const isThirdPartyError = (error, filename = "") => {
  if (!error) return false;

  const errorMessage = typeof error === "string" ? error : error.message || "";
  const errorStack = error?.stack || "";
  const sourceFile = filename || error?.fileName || "";

  // Check for blob URLs (browser extensions, service workers)
  if (errorStack.includes("blob:") || sourceFile.includes("blob:")) {
    return true;
  }

  // Check for zone.js errors (runTask, invokeTask, etc.)
  if (errorStack.includes("runTask@") || 
      errorStack.includes("invokeTask@") || 
      errorStack.includes("zone.js") ||
      errorStack.includes("ZoneDelegate") ||
      errorStack.includes("Zone.run")) {
    return true;
  }

  // Check if error message matches third-party patterns
  const messageMatch = THIRD_PARTY_ERROR_PATTERNS.some((pattern) =>
    errorMessage.toLowerCase().includes(pattern.toLowerCase())
  );

  // Check if error stack contains third-party domains
  const stackMatch = THIRD_PARTY_DOMAINS.some((domain) => errorStack.includes(domain) || sourceFile.includes(domain));

  // Special check for GTM/GA "Failed to fetch" errors
  const isGTMFetchError =
    errorMessage.includes("Failed to fetch") &&
    (errorStack.includes("googletagmanager.com") ||
      errorStack.includes("gtag/js") ||
      errorStack.includes("google-analytics.com") ||
      sourceFile.includes("googletagmanager.com") ||
      sourceFile.includes("gtag/js"));

  // Special check for SigNoz/OpenTelemetry internal errors
  const isSigNozInternalError =
    errorStack.includes("BatchSpanProcessorBase") ||
    errorStack.includes("BatchSpanProcessor") ||
    errorStack.includes("zone.js") ||
    (errorMessage.includes("Timeout") && errorStack.includes("timer (zone.js"));

  return messageMatch || stackMatch || isGTMFetchError || isSigNozInternalError;
};

/**
 * Check if URL is from third-party domain
 * @param {string} url - URL to check
 * @returns {boolean} - True if URL is third-party
 */
export const isThirdPartyUrl = (url) => {
  if (!url) return false;

  return THIRD_PARTY_DOMAINS.some((domain) => url.includes(domain));
};

/**
 * Safe wrapper untuk third-party script execution
 * @param {Function} fn - Function to execute
 * @param {Object} options - Options
 * @returns {any} - Function result or fallback
 */
export const safeThirdPartyExecution = (fn, options = {}) => {
  const { fallback = null, silent = true } = options;

  try {
    return fn();
  } catch (error) {
    if (isThirdPartyError(error)) {
      if (!silent) {
        console.warn("⚠️ Third-party script error (filtered):", error.message);
      }
      return fallback;
    }
    // Re-throw if not third-party error
    throw error;
  }
};

/**
 * Setup global filter untuk third-party errors
 */
export const setupThirdPartyErrorFilter = () => {
  // Override console.error untuk filter third-party errors
  const originalConsoleError = console.error;
  console.error = function (...args) {
    const errorMessage = args.join(" ");

    // Extra aggressive filtering for SignOz timeout errors
    if (errorMessage.includes("Timeout") && 
        (errorMessage.includes("zone.js") || 
         errorMessage.includes("BatchSpanProcessor") ||
         errorMessage.includes("bundle.js"))) {
      // Completely suppress - don't even log as warning
      return;
    }

    if (isThirdPartyError(errorMessage)) {
      console.warn("⚠️ Third-party error (filtered):", ...args);
      return;
    }

    originalConsoleError.apply(console, args);
  };

  // Add global error listener
  window.addEventListener(
    "error",
    (event) => {
      if (isThirdPartyError(event.error || event.message, event.filename)) {
        event.preventDefault();
        console.warn("⚠️ Third-party error caught:", {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        });
        return false;
      }
    },
    true // Use capture phase
  );

  console.log("✅ Third-party error filter initialized");
};

/**
 * Get error source information
 * @param {Error} error - Error object
 * @returns {Object} - Source information
 */
export const getErrorSource = (error) => {
  if (!error) return { isThirdParty: false, domain: null };

  const stack = error.stack || "";
  const fileName = error.fileName || "";

  for (const domain of THIRD_PARTY_DOMAINS) {
    if (stack.includes(domain) || fileName.includes(domain)) {
      return {
        isThirdParty: true,
        domain: domain,
        message: error.message,
      };
    }
  }

  return {
    isThirdParty: false,
    domain: null,
    message: error.message,
  };
};

/**
 * Log third-party error dengan detail
 * @param {Error} error - Error object
 * @param {Object} context - Additional context
 */
export const logThirdPartyError = (error, context = {}) => {
  const source = getErrorSource(error);

  if (source.isThirdParty) {
    console.warn("⚠️ Third-Party Error Detected:", {
      domain: source.domain,
      message: source.message,
      context: context,
      timestamp: new Date().toISOString(),
    });
  }
};

/**
 * Create error boundary untuk third-party scripts
 * @param {Function} fn - Function to wrap
 * @returns {Function} - Wrapped function
 */
export const createThirdPartyBoundary = (fn) => {
  return function (...args) {
    try {
      return fn.apply(this, args);
    } catch (error) {
      if (isThirdPartyError(error)) {
        logThirdPartyError(error, { function: fn.name, args });
        return undefined;
      }
      throw error;
    }
  };
};

export default {
  isThirdPartyError,
  isThirdPartyUrl,
  safeThirdPartyExecution,
  setupThirdPartyErrorFilter,
  getErrorSource,
  logThirdPartyError,
  createThirdPartyBoundary,
  THIRD_PARTY_DOMAINS,
  THIRD_PARTY_ERROR_PATTERNS,
};
